import { useEffect, useRef, useState, useCallback } from 'react'
import { X, Lock, Unlock, MessageSquare, Loader2, AlertCircle, Eye, EyeOff, MousePointer2 } from 'lucide-react'

/**
 * Admin-side viewer for a live remote-control session with one kiosk.
 *
 * The signalling itself (RC_START / RC_SIGNAL / RC_STOP) rides the Global
 * Monitor's existing WebSocket, this component only owns the WebRTC side and
 * the input capture. GlobalMonitor hands it inbound signals through
 * `onInboundRef` (a ref it fills in) rather than passing the socket down, so
 * this stays reusable without re-plumbing connection state.
 */

export interface RcInboundPayload {
  kind: 'offer' | 'answer' | 'ice' | 'ended' | 'input-warning'
  sdp?: RTCSessionDescriptionInit
  candidate?: RTCIceCandidateInit
  reason?: string
  message?: string
}

interface Props {
  deviceId: string
  label: string
  sessionId: string
  iceServers: RTCIceServer[]
  sendSignal: (payload: Record<string, unknown>) => void
  /** GlobalMonitor fills this with a callback so it can hand us inbound RC_SIGNAL/RC_ENDED messages. */
  onInboundRef: React.MutableRefObject<((payload: RcInboundPayload) => void) | null>
  onClose: (reason?: string) => void
  /** Whether this session was opened in unattended view mode (set by the toggle in GlobalMonitor before starting it). */
  initialUnattended?: boolean
}

const QUALITY_PRESETS = ['low', 'auto', 'high'] as const

export default function RemoteControlViewer({ label, sessionId, iceServers, sendSignal, onInboundRef, onClose, initialUnattended }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const dcRef = useRef<RTCDataChannel | null>(null)
  const [status, setStatus] = useState<'connecting' | 'live' | 'ended'>('connecting')
  const [error, setError] = useState<string | null>(null)
  const [inputWarning, setInputWarning] = useState<string | null>(null)
  const [locked, setLocked] = useState(!initialUnattended)
  // Unattended starts as a view without the maintenance overlay. Taking
  // control enables remote input separately from the kiosk's local input lock.
  const [unattended, setUnattended] = useState(!!initialUnattended)
  const [takingControl, setTakingControl] = useState(!initialUnattended)
  const canControl = !unattended || takingControl
  const [quality, setQuality] = useState<typeof QUALITY_PRESETS[number]>('auto')
  const [editingMessage, setEditingMessage] = useState(false)
  const [messageText, setMessageText] = useState(
    'Er wordt op afstand technisch onderhoud uitgevoerd. We zijn hier zo snel mogelijk weer voor u.'
  )

  const send = useCallback((payload: Record<string, unknown>) => {
    sendSignal({ sessionId, payload })
  }, [sendSignal, sessionId])

  useEffect(() => {
    const pc = new RTCPeerConnection({ iceServers })
    pcRef.current = pc

    // Nothing here used to time out: a device that never answers (crashed
    // before sending its offer, or stuck behind a NAT the relay can't fix)
    // left this spinning on "Negotiating..." forever with no way out but
    // closing the tab. This is the backstop.
    const stuckTimer = setTimeout(() => {
      if (pcRef.current === pc && pc.connectionState !== 'connected') {
        setStatus('ended')
        setError('The device never completed the connection. It may be offline, running an older client, or unreachable through the relay.')
      }
    }, 20000)

    pc.ontrack = (e) => {
      if (videoRef.current) videoRef.current.srcObject = e.streams[0]
    }
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ kind: 'ice', candidate: e.candidate })
    }
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') setStatus('live')
      if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
        setStatus('ended')
      }
    }
    pc.ondatachannel = (e) => {
      dcRef.current = e.channel
      e.channel.onopen = () => {
        const hb = setInterval(() => {
          try { e.channel.send(JSON.stringify({ t: 'hb' })) } catch { /* channel closing */ }
        }, 3000)
        e.channel.onclose = () => clearInterval(hb)
      }
    }

    // A failure anywhere in here used to only reach console.error, the UI
    // text is driven by `status` alone, so a thrown error left the screen
    // stuck on "Negotiating..." forever with no sign anything had failed.
    const fail = (context: string, err: unknown) => {
      console.error(context, err)
      setStatus('ended')
      setError(`${context}: ${err instanceof Error ? err.message : String(err)}`)
    }

    // One handler for every inbound signal, rather than two effects each
    // wrapping the other's onInboundRef, that pattern depends on effect
    // *ordering* on mount, which React only guarantees loosely (and this app
    // runs in StrictMode, which double-invokes effects on mount), so it's not
    // worth the fragility for something this load-bearing.
    onInboundRef.current = async (payload) => {
      try {
        if (payload.kind === 'offer' && payload.sdp) {
          await pc.setRemoteDescription(payload.sdp)
          const answer = await pc.createAnswer()
          await pc.setLocalDescription(answer)
          send({ kind: 'answer', sdp: pc.localDescription })
        } else if (payload.kind === 'ice' && payload.candidate) {
          await pc.addIceCandidate(payload.candidate)
        } else if (payload.kind === 'ended') {
          setStatus('ended')
          setError(payload.reason === 'disabled-on-device' ? 'Remote control is turned off on this device.' : payload.reason || null)
        } else if (payload.kind === 'input-warning') {
          // Non-fatal: the screen share still works, only input injection
          // doesn't, so the session stays open with a visible reason instead
          // of silently doing nothing when the operator clicks.
          setInputWarning(payload.message || 'Input control is unavailable on this device.')
        }
      } catch (err) {
        fail(payload.kind === 'offer' ? 'Could not negotiate the connection' : 'Connection error', err)
      }
    }

    return () => {
      clearTimeout(stuckTimer)
      onInboundRef.current = null
      try { pc.close() } catch { /* already closing */ }
      pcRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stop = useCallback((reason?: string) => {
    send({ kind: 'stop' })
    onClose(reason)
  }, [send, onClose])

  const toggleLock = () => {
    if (!unattended) return
    const next = !locked
    setLocked(next)
    send({ kind: 'lock', on: next })
  }

  const toggleUnattended = () => {
    const next = !unattended
    setUnattended(next)
    setTakingControl(!next)
    setEditingMessage(false)
    if (next) setLocked(false)
    else setLocked(true)
    send({ kind: 'mode', viewOnly: next, lock: !next })
  }

  const toggleControl = () => {
    setTakingControl(!takingControl)
    if (!takingControl) videoRef.current?.focus()
  }

  const sendMessage = () => {
    send({ kind: 'message', title: 'Een ogenblik...', text: messageText })
    setEditingMessage(false)
  }

  const setQualityPreset = (q: typeof QUALITY_PRESETS[number]) => {
    setQuality(q)
    try { dcRef.current?.send(JSON.stringify({ t: 'q', preset: q })) } catch { /* channel not open yet */ }
  }

  // Forward local mouse/keyboard/wheel to the kiosk via the data channel. The
  // video element's natural size maps to the kiosk's screen coordinates.
  const toRemoteCoords = (e: React.MouseEvent) => {
    const el = videoRef.current
    if (!el || !el.videoWidth || !el.videoHeight) return null
    const rect = el.getBoundingClientRect()
    // The element is styled `object-contain`, so the video is letterboxed
    // inside `rect` rather than filling it, mapping directly against
    // `rect.width`/`height` put every click off by however much blank bar
    // the aspect-ratio mismatch produced. Compute the actual drawn rectangle
    // first, then clamp, so a click just outside it still lands at the edge
    // instead of being discarded or wildly out of range on the kiosk.
    const videoAspect = el.videoWidth / el.videoHeight
    const boxAspect = rect.width / rect.height
    let drawnWidth = rect.width
    let drawnHeight = rect.height
    let offsetX = 0
    let offsetY = 0
    if (videoAspect > boxAspect) {
      drawnHeight = rect.width / videoAspect
      offsetY = (rect.height - drawnHeight) / 2
    } else {
      drawnWidth = rect.height * videoAspect
      offsetX = (rect.width - drawnWidth) / 2
    }
    const relX = Math.min(1, Math.max(0, (e.clientX - rect.left - offsetX) / drawnWidth))
    const relY = Math.min(1, Math.max(0, (e.clientY - rect.top - offsetY) / drawnHeight))
    return { x: relX, y: relY }
  }
  const sendInput = (evt: Record<string, unknown>) => {
    // Heartbeats and quality changes keep working while watching. Remote
    // input in unattended mode requires explicitly taking control.
    if (!canControl || status !== 'live') return
    try { dcRef.current?.readyState === 'open' && dcRef.current.send(JSON.stringify(evt)) } catch { /* best effort */ }
  }

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-black">
      <div className="flex items-center justify-between gap-3 bg-zinc-900 px-4 py-2.5 text-white">
        <div className="flex items-center gap-2 text-sm">
          <span className={`h-2 w-2 rounded-full ${status === 'live' ? 'bg-green-500 animate-pulse' : status === 'ended' ? 'bg-red-500' : 'bg-amber-500 animate-pulse'}`} />
          <span className="font-medium">{label}</span>
          <span className="text-zinc-400">
            {status === 'connecting' && 'Connecting…'}
            {status === 'live' && (unattended ? (takingControl ? 'Live, unattended control' : 'Live, unattended view') : 'Live')}
            {status === 'ended' && 'Session ended'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={quality}
            onChange={(e) => setQualityPreset(e.target.value as any)}
            className="rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-white"
            title="Stream quality"
          >
            {QUALITY_PRESETS.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
          <button
            onClick={toggleUnattended}
            aria-pressed={unattended}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium ${unattended ? 'bg-sky-500/20 text-sky-400' : 'bg-zinc-800 text-zinc-300'}`}
            title={unattended
              ? 'No maintenance overlay. Use Take control to enable remote mouse and keyboard input.'
              : 'Switch to unattended view: hide the overlay and release input, for monitoring without interrupting the booth'}
          >
            {unattended ? <Eye size={13} /> : <EyeOff size={13} />} Unattended
          </button>
          {unattended && (
            <button
              onClick={toggleControl}
              disabled={status !== 'live'}
              aria-pressed={takingControl}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium disabled:opacity-40 ${takingControl ? 'bg-sky-500/20 text-sky-400' : 'bg-zinc-800 text-zinc-300'}`}
              title={takingControl ? 'Stop remote input and return to watching' : 'Control the kiosk without showing the maintenance overlay'}
            >
              <MousePointer2 size={13} /> {takingControl ? 'Stop control' : 'Take control'}
            </button>
          )}
          <button
            onClick={toggleLock}
            disabled={!unattended}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium disabled:opacity-40 ${locked ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-300'}`}
            title={!unattended ? 'Local input stays locked during maintenance. Switch to unattended mode to unlock it.' : (locked ? 'Local input is blocked on the kiosk' : 'Local input is not blocked')}
          >
            {locked ? <Lock size={13} /> : <Unlock size={13} />} {locked ? 'Locked' : 'Unlocked'}
          </button>
          <button
            onClick={() => setEditingMessage((v) => !v)}
            disabled={unattended}
            className="flex items-center gap-1.5 rounded-md bg-zinc-800 px-2.5 py-1.5 text-xs font-medium text-zinc-300 disabled:opacity-40"
            title={unattended ? 'Not available in unattended view' : undefined}
          >
            <MessageSquare size={13} /> Message
          </button>
          <button
            onClick={() => stop('admin-closed')}
            className="flex items-center gap-1.5 rounded-md bg-red-500/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500"
          >
            <X size={13} /> End session
          </button>
        </div>
      </div>

      {editingMessage && (
        <div className="flex items-center gap-2 bg-zinc-800 px-4 py-2">
          <input
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            className="flex-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-white"
            placeholder="Message shown on the kiosk screen"
          />
          <button onClick={sendMessage} className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
            Update
          </button>
        </div>
      )}

      {inputWarning && (
        <div className="flex items-center justify-between gap-3 bg-amber-500/15 px-4 py-2 text-xs text-amber-400">
          <span className="flex items-center gap-2"><AlertCircle size={13} /> {inputWarning} You can still watch the screen.</span>
          <button onClick={() => setInputWarning(null)} className="text-amber-400/70 hover:text-amber-400">✕</button>
        </div>
      )}

      <div className="relative flex-1 overflow-hidden bg-zinc-950">
        {status !== 'live' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-400">
            {status === 'connecting' ? <Loader2 className="animate-spin" /> : <AlertCircle />}
            <p className="text-sm">{status === 'connecting' ? 'Negotiating the connection…' : (error || 'The session ended.')}</p>
          </div>
        )}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`h-full w-full object-contain ${status === 'live' ? '' : 'opacity-0'}`}
          onMouseMove={(e) => { const p = toRemoteCoords(e); if (p) sendInput({ t: 'mm', rx: p.x, ry: p.y }) }}
          onMouseDown={(e) => sendInput({ t: 'mb', b: e.button, d: true })}
          onMouseUp={(e) => sendInput({ t: 'mb', b: e.button, d: false })}
          onWheel={(e) => { e.preventDefault(); sendInput({ t: 'wh', dx: e.deltaX, dy: e.deltaY }) }}
          onContextMenu={(e) => e.preventDefault()}
          tabIndex={0}
          onKeyDown={(e) => { e.preventDefault(); sendInput({ t: 'kb', code: e.code, k: e.key, d: true }) }}
          onKeyUp={(e) => { e.preventDefault(); sendInput({ t: 'kb', code: e.code, k: e.key, d: false }) }}
        />
      </div>
    </div>
  )
}
